// apps/web/src/components/RequireCanAuthor.tsx
//
// Phase 10 Plan 10-10 — route guard for authoring surfaces (Surface 1
// /recipes/extract per 10-UI-SPEC §"Phase 10 surface routes": authenticated +
// can_author).
//
// AUTH MODEL / DEFENSE-IN-DEPTH:
//   The AUTHORITATIVE authoring gate is enforced SERVER-SIDE: POST
//   /api/recipes/extract returns 403 for a session that lacks authoring
//   permission, and <ExtractForm> surfaces that as the "authoring_not_permitted"
//   failure strip. This guard is the client-side defense-in-depth layer.
//
//   The project does not yet model `can_author` on the client — there is no
//   /api/me endpoint and no can_author concept in apps/api at this phase. So
//   this guard gates on the Better-Auth SESSION (the real, existing auth
//   substrate via useSession()): an unauthenticated visitor is redirected to
//   sign-in with a returnTo. When a future plan adds /api/me + can_author, the
//   can_author short-circuit slots in at the marked seam below WITHOUT changing
//   the route wiring (this component stays the single mount point).
//
// Mirrors the intent of the plan's RequireCanAuthor wrapper (10-10-PLAN.md
// <action>) while staying honest about the data that actually exists today.

import { Loader2 } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "@/lib/auth-client";

export interface RequireCanAuthorProps {
  children: React.ReactNode;
}

export function RequireCanAuthor({ children }: RequireCanAuthorProps): React.ReactElement {
  const location = useLocation();
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <main aria-label="Checking access" className="flex h-screen items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 motion-safe:animate-spin" aria-hidden />
          <span>Checking access…</span>
        </div>
      </main>
    );
  }

  // Unauthenticated → bounce to sign-in, preserving the intended destination.
  // WR-04: URL-encode the returnTo. location.search can itself contain &/=/#,
  // which would parameter-split or fragment-truncate the constructed sign-in URL
  // if interpolated raw. computeAllowedReturnTo() sanitizes on the consuming
  // side, but emitting an unencoded value here is fragile and can defeat the
  // round-trip; encodeURIComponent keeps the whole path+query as one opaque
  // returnTo value.
  if (session === null || session === undefined) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/auth/sign-in?returnTo=${returnTo}`} replace />;
  }

  // SEAM (future /api/me + can_author): when the client learns `can_author`,
  // add here:
  //   if (session.user.can_author !== true) {
  //     return <Navigate to="/account" replace />;  // + one-off toast
  //   }
  // Until then the server's 403 on /api/recipes/extract is the authoritative
  // can_author gate (surfaced by <ExtractForm> as "authoring_not_permitted").

  return <>{children}</>;
}
RequireCanAuthor.displayName = "RequireCanAuthor";
